export interface BranchQboDepositAccount {
  id: string;
  name: string;
  accountType?: string;
  accountSubType?: string;
}

export interface BranchContractInfo {
  language?: 'fa' | 'ps' | 'mazar';
  phone?: string;
  address?: string;
}

export interface BranchReceiptInfo {
  template?: 'text' | 'full';
  phone?: string;
  address?: string;
}

export interface BranchVisp {
  vispId: number;
  vispName: string;
}

export interface Branch {
  _id: string;
  name: string;
  location: string;
  // Short abbreviation used for invoice/payment DocNumber prefixes (e.g. HRT, TGH).
  abbr?: string;
  // Starting contract ID sequence for this branch
  initialContractId?: number | null;
  centerId?: number;
  centerName?: string;
  rs_Id?: string;
  parentId?: string;
  parentName?: string;
  qboLocationId?: string;
  qboLocationName?: string;
  qboDepositAccounts?: BranchQboDepositAccount[];
  contractInfo?: BranchContractInfo;
  receiptInfo?: BranchReceiptInfo;
  visps?: BranchVisp[];
  defaultVispId?: number;
  defaultVispName?: string;
  seq?: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Notification {
  message: string;
  read: boolean;
  seen: boolean;
  relatedModel: string;
  relatedId: string;
  createdAt: Date;
}

export interface User {
  _id: string;
  cartId: number;
  name: string;
  email: string;
  role: "admin" | "noc" | "installer" | "financeAdmin" | "sales" | "creditChecker" | "cashier";
  branch_id: string | Branch;
  rs_Id?: string;
  notifications: Notification[];
  refreshToken?: string;
  refreshTokens?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  password?: string;
  tasks?: AssignedTask[];
  qboUserId?: string;
  qboUserName?: string;
  isVerified?: boolean;
  invitedAt?: Date;
  isEnabled?: boolean;
  permissions?: Record<string, boolean>;
}

export interface Equipment {
  _id: string;
  name: string;
  description?: string;
  unit: string;
  sku: string;
  sellingPrice: number;
  account?: string;
  costPrice?: number;
  expenseAccount?: string;
  seq?: number;
  type?: 'antenna' | 'router' | 'cable' | 'other';
  qboItemId?: string;
}

export interface Service {
  Service_Id: string;
  ServiceName: string;
  Description: string;
  Price: string;
  ServiceType: string;
  ISEnable: string;
  UserChoosable: string;
  ResellerChoosable: string;
}

export interface Customer {
  _id?: string;
  customerId?: number;
  contractId?: number | string;
  username: string;
  password: string;
  custType: "individual" | "business";
  name: string;
  nameFa?: string;
  lastName?: string;
  lastNameFa?: string;
  email?: string;
  nid?: number;
  address?: string;
  companyName?: string;
  licenseNo?: string | number;
  activity?: string;
  phones?: string[];
  /** Primary line (CRM model); also see `phones` */
  phone?: string;
  branch_id?: string | Branch;
  date?: Date;
  customFields?: Array<{
    key: string;
    value: string;
    _id?: string;
  }>;
  internetUsage?: string;
  internetUsagePurpose?: string;
  previousPackage?: string;
  familiarityWithUs?: string;
  terminationReason?: string;
  isReturningCustomer?: boolean;
  isPrinted?: boolean;
  isSigned?: boolean;
  /** Customer already has their own CPE — no devices sold via this form */
  customerHasOwnDevice?: boolean;
  devices?: Array<{
    device?: string | Equipment | null;
    quantity?: number;
    unitPrice?: number;
    paid?: boolean;
    purchaseDate?: Date;
    checked?: boolean;
    /** Set when devices are saved from the sales customer form */
    addedBySales?: boolean;
    /** Sold vs leased (قرضی) equipment */
    provision?: 'sold' | 'leased';
    checklistId?: string;
    deviceName?: string;
    deviceDescription?: string;
    deviceUnit?: string;
    deviceSku?: string;
    deviceSellingPrice?: number;
    deviceAccount?: string;
    deviceCostPrice?: number;
    deviceExpenseAccount?: string;
    _id?: string;
  }>;
  /** Package frozen at sales contract — not changed by installer */
  contractService?: {
    service: Service;
    startDate?: Date;
    endDate?: Date;
    serviceStatus?: string;
    customPrice?: number;
    priceBasis?: "gross" | "net";
    discount?: number;
    discountType?: "amount" | "percent";
    discountScope?: "current" | "any";
    discountPrice?: number;
    isApproved?: boolean;
  };
  currentService?: {
    service: Service;
    startDate?: Date;
    endDate?: Date;
    paid: boolean;
    User_ServiceBase_Id?: string;
    serviceStatus?: string;
    customPrice?: number;
    /** `net` = customPrice is amount after discount; missing/`gross` = legacy line price before discount */
    priceBasis?: "gross" | "net";
    discount?: number;
    discountType?: "amount" | "percent";
    /** Applied discount amount in currency */
    discountPrice?: number;
    /** Discount approval flag; false when a new discount is saved */
    isApproved?: boolean;
  };
  paymentHistory?: any[];
  deltaSibUserId?: string;
  customerStatus?: "demo" | "final";
  displayName?: string;
  quickbooksCustomerId?: string; 
  fullName?: string; 
  nationalCode?: string; 
  mobile?: string; 
  isLinked?: boolean;
  matchStatus?: string;
  deltaUserId?: number;
  vispId?: number; 
  centerId?: number; 
  qbActive?: boolean; // QuickBooks active status
  creditInfo?: any[]; // Credit information
  installationStatus?: string;
  note?: string;
  __v?: number;
}

export const CustomerMessages = {
  CREATE_SUCCESS: (name: string) => `${name} has been successfully added as a customer!`,
  CREATE_ERROR: (name: string) => `Failed to add ${name} as a customer. Please try again.`,
  UPDATE_SUCCESS: (name: string) => `${name}'s information has been updated successfully!`,
  UPDATE_ERROR: (name: string) => `Failed to update ${name}'s information.`,
  DELETE_SUCCESS: (name: string) => `${name} has been removed from customers successfully!`,
  DELETE_ERROR: (name: string) => `Failed to remove ${name} from customers.`,
  DEVICE_UPDATE_SUCCESS: (name: string) => `${name}'s devices have been updated successfully!`,
  DEVICE_UPDATE_ERROR: (name: string) => `Failed to update ${name}'s devices.`,
  USERNAME_EXISTS: 'Username already exists. Please choose a different username.',
  INVALID_USERNAME: 'Invalid username. Username must be unique and contain only letters, numbers, and underscores.',
} as const;

export interface Installation {
  _id: string;
  seq?: number;
  customerId: any;
  taskId?: string;
  status: "awaiting" | "pending" | "in-progress" | "done" | "failed";
  type: string;
  date: Date;
  result: {
    site?: string;
    signalStrength?: number;
    signalToNoise?: number;
    note: string;
    cancelDetails?: string;
    gps?: {
      lat: string | number;
      lng: string | number;
    };
    /** @deprecated legacy */
    signal?: number;
    ccq?: number;
    base?: string;
  };
  devices: {
    router: string;
    routerMac: string;
    antenna: string;
    antennaMac: string;
    source: string;
    equipmentItems?: Array<{
      device: string | { _id: string };
      quantity: number;
      isUsed?: boolean;
      source?: 'sales' | 'installer';
    }>;
  };
  packageId: string;
  approved?: boolean;
  /** Installer user ref — set when a task is assigned */
  installerId?: string | User;
  /** Exact date/time when task was assigned to installer */
  assignedAt?: string | Date | null;
  assignedTask?: {
    _id: string;
    assignedAt?: string | Date;
    status?: string;
  } | null;
  photos?: Array<{ url: string; filename: string }>;
}

export interface AssignedTask {
  _id: string;
  seq?: number;
  userId: string | User;
  installationId: string | Installation;
  assignedAt: Date;
  status: "assigned" | "in-progress" | "completed" | "cancelled";
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Checklist {
  _id: string;
  seq?: number;
  contractId?: string;
  isDuplicate?: boolean;
  customerId?: string | Customer; 
  qboCustomerLinkId?: {
    _id: string;
    qbId: string;
    displayName: string;
    contractId?: string;
    crmCustomerId?: string;
    deltaSibUserId?: number;
    /** DeltaSIB billing user id (same family as checklist.deltaUserId) */
    deltaUserId?: number;
    deltaUsername?: string;
    vispId?: number;
    vispName?: string;
    centerId?: number;
    centerName?: string;
    delta?: {
      username?: string;
      userId?: number;
    };
    qboLocation?: {
      id: string | null;
      name: string | null;
    };
    qboClass?: {
      id: string | null;
      name: string | null;
    };
  };
  qbCustomerId?: string;
  hasQboLink?: boolean; 
  deltaUserId?: number;
  deltaUsername?: string;
  totalPrice: number;
  type: 'device' | 'service';
  status: 'pending' | 'done' | 'others';
  /** NOC approved installation devices — required before device invoice sync */
  nocApproved?: boolean;
  isHidden: boolean;
  serviceBaseCreatedAt?: Date;
  service?: {
    service: {
      Service_Id: string;
      ServiceName: string;
      Description: string;
      Price: string;
      ServiceType: string;
      ISEnable: string;
      UserChoosable: string;
      ResellerChoosable: string;
    };
    startDate: Date;
    endDate: Date;
    serviceStatus: string;
    User_ServiceBase_Id: string;
    deltaUserId?: number;
    deltaUsername?: string;
    vispId?: number;
    vispName?: string;
    centerId?: number;
    centerName?: string;
    payPlan?: string;
    servicePrice?: string;
    payPrice?: string;
  };
  devices?: Array<{
    equipmentId: string | Equipment;
    quantity: number;
    unitPrice?: number;
    _id: string;
  }>;
  branch_id?: string | Branch;
  rs_Id?: string | { _id?: string; abbr?: string; name?: string };
  vispId?: number;
  vispName?: string;
  centerId?: number;
  centerName?: string;
  sentToQuickbooks: boolean;
  customerCreatedInQB: boolean;
  invoiceCreated: boolean;
  isManual: boolean;
  reason?: string;
  // New analysis fields
  serviceChangeType?: 'new' | 'recharge' | 'change' | null;
  hasDiscount?: boolean;
  discountAmount?: number;
  invoiceMemo?: string;
  lastInvoice?: {
    id: string;
    docNumber: string;
    txnDate: string;
    totalAmt: number;
    serviceName: string;
    discountAmount: number;
  };
  lastServiceId?: number;
  hasUnappliedPayment?: boolean;
  unappliedPaymentAmount?: number;
  unappliedPayments?: Array<{
    id: string;
    txnDate: string;
    totalAmt: number;
    unappliedAmt: number;
  }>;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface FormData {
  custType: 'individual' | 'business';
  username: string;
  password: string;
  date: Date;
  name: string;
  lastName: string;
  email: string;
  address: string;
  phones: string[];
  customFields: { key: string; value: string }[];
  internetUsagePurpose: string;
  familiarityWithUs: string;
  previousPackage: string;
  internetUsage: string;
  terminationReason: string;
  customerStatus: 'demo' | 'final';
  nid?: string;
  companyName?: string;
  licenseNo?: string;
  activity?: string;
  currentService?: {
    service: Service;
    startDate: Date;
    endDate?: Date;
    paid: boolean;
  };
}

export interface FetchChecklistParams {
  page: number;
  limit: number;
  status: string;
  type: string;
  date?: string;
  branch?: string;
  [key: string]: string | number | undefined;
}